import * as Yup from "yup";
import { QUESTION_TYPE_ID } from "../Submission/constants";

const isNotRemoved = (isRemove?: number) => {
	return isRemove === 0 || isRemove == null;
};

/**
 * Wrap a schema to skip validation if isRemove = 1
 * Preserves field paths for Formik
 */
export const withIsRemove = <T extends Yup.AnyObjectSchema>(schema: T): T => {
	return Yup.object()
		.shape({
			isRemove: Yup.number().oneOf([0, 1]).required(),
		})
		.test("skip-if-removed", "", function (value) {
			// If value is undefined or null, skip
			if (!value) {
				return true;
			}

			// If isRemove = 1, skip validation
			if (value.isRemove === 1) {
				return true;
			}

			// Otherwise validate the original schema
			try {
				schema.validateSync(value, {
					abortEarly: false,
					context: this.options.context,
				});
				return true;
			} catch (err: any) {
				if (err.inner && err.inner.length) {
					// Create ValidationErrors that preserve inner array
					const innerErrors = err.inner.map((e: any) => {
						const fullPath = this.path ? `${this.path}.${e.path}` : e.path;
						return new Yup.ValidationError(e.message, e.value, fullPath);
					});

					// ❗ Throw one ValidationError with all inner errors
					throw new Yup.ValidationError(innerErrors);
				}

				throw err;
			}
		}) as T;
};

const typesChecker = (type: number) => {
	switch (type) {
		case QUESTION_TYPE_ID.SINGLE_OPTION:
			return "Option";
		case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
			return "Checkbox";
		case QUESTION_TYPE_ID.DROP_DOWN:
			return "Selection";
		default:
			return "Option";
	}
};

/**
 * returns Yup validation config for all question types
 * @param questionTypeId
 * @param value being validated
 * @returns
 */
const validateByQuestionType = (questionTypeId: number) => {
	switch (questionTypeId) {
		case QUESTION_TYPE_ID.CORPORATE_DOCUMENTS:
			return Yup.object().shape({
				type: Yup.string().required(),
				allow_document_categories: Yup.array()
					.min(1, "At least one document category is required")
					.of(
						Yup.object().shape({
							category_id: Yup.mixed().required(
								"Document category is required"
							),
							subcategory_id: Yup.mixed().nullable(),
						})
					)
					.required("Document categories are required"),
			});

		case QUESTION_TYPE_ID.TABLE: {
			let index = 0;
			return Yup.array().of(
				Yup.object().shape({
					header: Yup.string().required("Header value is required"),
					values: Yup.array().when("$", (_, schema) => {
						if (index === 0) {
							index++;
							return Yup.array().of(
								Yup.mixed().required("Question for Supplier is required")
							);
						}
						return Yup.array().of(Yup.string().nullable());
					}),
				})
			);
		}

		case QUESTION_TYPE_ID.TABLE_ROWS:
			return Yup.array()
				.of(
					Yup.object({
						header: Yup.string()
							.trim()
							.required("Column header text is required"),
					})
				)
				.min(1, "At least one column is required");

		case QUESTION_TYPE_ID.SINGLE_OPTION:
		case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
		case QUESTION_TYPE_ID.DROP_DOWN:
			return Yup.array()
				.of(
					Yup.string().required(
						`${typesChecker(questionTypeId)} name is required`
					)
				)
				.min(1, "At least one option is required");

		default:
			return Yup.mixed().nullable();
	}
};

export const generateQuestionSchema = () => {
	// initialise for all question types
	// name - checking isRemove is not set to true
	// then we want a global isRemove check - if this is set then no validation should be done
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().when("isRemove", {
			is: (val) => isNotRemoved(val),
			then: Yup.string().required("Question name is required"),
			otherwise: Yup.string().nullable(),
		}),
		fields: Yup.mixed().when(["type", "isRemove"], (type, isRemove, schema) => {
			// if removing the question, no more validation needed
			if (isRemove === 1) {
				return Yup.mixed().nullable();
			}

			return validateByQuestionType(parseInt(type));
		}),
		sort_order: Yup.number().nullable(),
		required: Yup.number().oneOf([0, 1]).required(),
		type: Yup.string().required("Please select the question type"),
	});
};

const generateQuoteItemSchema = (): Yup.AnySchema => {
	// Uses .when() instead of withIsRemove so Yup 0.28 produces properly nested
	// errors that Formik can map to individual fields (withIsRemove's .test()
	// throws a wrapped ValidationError whose inner errors are not flattened by
	// Yup 0.28, so Formik only sees a single opaque error per table/row).
	return Yup.object().shape({
		id: Yup.string().nullable(),
		isRemove: Yup.number().oneOf([0, 1]),
		name: Yup.string().when("isRemove", {
			is: (val: number) => isNotRemoved(val),
			then: Yup.string().required("Item name is required"),
			otherwise: Yup.string().nullable(),
		}),
		unit_id: Yup.mixed().when("isRemove", {
			is: (val: number) => isNotRemoved(val),
			then: Yup.mixed()
				.test("unit_id_required", "Unit of Measurement is not selected", (val) => {
					return val !== null && val !== undefined && val !== "" && val !== 0;
				}),
			otherwise: Yup.mixed().nullable(),
		}),
		sort_order: Yup.number().nullable(),
	});
};

export const generateQuoteSchema = (): Yup.AnySchema => {
	return Yup.object().shape({
		id: Yup.string().nullable(),
		isRemove: Yup.number().oneOf([0, 1]).required(),
		name: Yup.string().when("isRemove", {
			is: (val: number) => isNotRemoved(val),
			then: Yup.string().required("Header is required"),
			otherwise: Yup.string().nullable(),
		}),
		description: Yup.string().nullable(),
		sort_order: Yup.number().nullable(),
		required: Yup.number().when("isRemove", {
			is: (val: number) => isNotRemoved(val),
			then: Yup.number().oneOf([0, 1]).required(),
			otherwise: Yup.number().nullable(),
		}),
		items: Yup.array().when("isRemove", {
			is: (val: number) => isNotRemoved(val),
			then: Yup.array()
				.test("rowTotal", "At least one row is required", function (value) {
					return (value?.filter((item: { isRemove?: number }) => !item.isRemove)?.length ?? 0) > 0;
				})
				.of(generateQuoteItemSchema()),
			otherwise: Yup.array().nullable(),
		}),
	});
};

const generateGuestSchema = () => {
	return Yup.object().shape({
		name: Yup.string().required("Name is required"),
		email: Yup.string().email("Invalid email").required("Email is required"),
	});
};

export const generateGuestInviteSchema = () => {
	return {
		guest: Yup.array()
			.of(generateGuestSchema())
			.test(
				"unique-emails",
				"You have invited the same email address more than once, please check and remove below.",
				(value) => {
					if (!value) {
						return true;
					}

					// check uniques
					const seen = new Set<string>();
					for (const guest of value) {
						if (!guest?.email) {
							continue;
						}
						const email = guest.email.toLowerCase();
						if (seen.has(email)) {
							return false;
						}
						seen.add(email);
					}
					return true;
				}
			),
		// Mirrors the server-side rule: an RFx must be sent to at least one
		// supplier or guest. Deliberately a separate field from `guest` rather
		// than another `.test()` on it — `guest`'s own error is read by
		// InviteForm to decorate the invite-guest panel, and a plain string
		// error here (as opposed to the per-row array shape InviteForm
		// expects) would render there too and outline a panel that isn't
		// itself invalid. `suppliers` and `guest` are both sibling fields on
		// the same object, read via `this.parent`.
		hasParticipants: Yup.mixed().test(
			"at-least-one-participant",
			"You have not yet selected any RFx Participants - please select one to send to or invite a guest user",
			function () {
				const suppliers = this.parent?.suppliers;
				const guest = this.parent?.guest;
				const hasGuest = Array.isArray(guest) && guest.length > 0;
				const hasSupplier = Array.isArray(suppliers) && suppliers.length > 0;
				return hasGuest || hasSupplier;
			}
		),
	};
};
